import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { createTraceContext, type TraceContext } from "../trace/context";
import { normalizeHttpError, type HttpClientError } from "./errors";

/** 统一拦截器可配置项。 */
export type HttpInterceptorsOptions = {
  getAccessToken?: () => string | null | undefined;
  getTraceContext?: () => TraceContext;
  onUnauthorized?: (error: HttpClientError) => void;
  onForbidden?: (error: HttpClientError) => void;
};

/** 注册统一请求/响应拦截器。 */
export function attachHttpInterceptors(
  client: AxiosInstance,
  options: HttpInterceptorsOptions = {},
): AxiosInstance {
  const resolveTraceContext = options.getTraceContext ?? createTraceContext;

  client.interceptors.request.use((config) => {
    const context = resolveTraceContext();
    setRequestHeader(config, "x-trace-id", context.traceId);
    setRequestHeader(config, "x-session-id", context.sessionId);

    const accessToken = options.getAccessToken?.();
    if (accessToken && accessToken.trim().length > 0) {
      setRequestHeader(config, "Authorization", `Bearer ${accessToken}`);
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const normalizedError = normalizeHttpError(error);
      if (normalizedError.statusCode === 401) {
        options.onUnauthorized?.(normalizedError);
      } else if (normalizedError.statusCode === 403) {
        options.onForbidden?.(normalizedError);
      }
      return Promise.reject(normalizedError);
    },
  );

  return client;
}

/** 安全写入请求头，兼容 AxiosHeaders 与普通对象。 */
function setRequestHeader(
  config: InternalAxiosRequestConfig,
  key: string,
  value: string | null | undefined,
): void {
  if (!value) {
    return;
  }

  if (typeof config.headers.set === "function") {
    config.headers.set(key, value);
    return;
  }

  config.headers[key] = value;
}
